import { Box, Divider, SxProps, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC } from 'react';
import SelectedFilters, { selectedFilters } from './selected-filters';
import { useMovieDetailsState, useSnack } from '@/logic/state';

import CinemasFilter from './cinemas-filter';
import ExperiencesFilter from './experiences-filter';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import ImageBoxFuture from '@/components/shared/image-box-future';

const NoFilters = '/icons/movie-finder/empty-filter-icon.svg';

interface MovieFilterProps {
  removeSelectedFilter: (filter: selectedFilters) => void;
  handleClearFilters: () => void;
  submitFilters: () => void;
}

export const MovieFilters: FC<MovieFilterProps> = ({
  submitFilters,
  removeSelectedFilter,
  handleClearFilters,
}) => {
  const [
    movieCinemasFilter,
    selectedCinemasFilter,
    movieExperiencesFilter,
    selectedExperiencesFilter,
    selectedFilters,
    isFetchingCinemas,
    isFetchingExperiences,
    filmCinemas,
    seeResultsDisabled,
    setSelectedCinemasFilter,
    setSelectedExperiencesFilter,
    isOfferApplied,
    isThereAtLeastOneAvailableShowTime,
  ] = useMovieDetailsState(
    s => [
      s.movieCinemasFilter,
      s.selectedCinemasFilter,
      s.movieExperiencesFilter,
      s.selectedExperiencesFilter,
      s.selectedFilters,
      s.isFetchingCinemas,
      s.isFetchingExperiences,
      s.filmCinemas,
      s.seeResultsDisabled,
      s.setSelectedCinemasFilter,
      s.setSelectedExperiencesFilter,
      s.isOfferApplied,
      s.isThereAtLeastOneAvailableShowTime,
    ],
    shallow
  );

  const EmptyMsg = (
    <FormattedMessage
      id={
        !isThereAtLeastOneAvailableShowTime
          ? 'movie_details.no_filters_can_be_Selected'
          : 'movie_details.offer.no_filters_can_be_Selected'
      }
    />
  );

  const theme = useTheme();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const intl = useIntl();

  const dividerSx: SxProps = {
    backgroundColor: theme.palette.grey[20],
  };

  const typographySx: SxProps = {
    color: theme.palette.grey[90],
    fontWeight: 400,
    fontSize: pxToRem(20),
    lineHeight: '30px',
  };

  const showDisabledFilterMessage = (): void => {
    setSnack({
      isOpen: true,
      message: intl.formatMessage({ id: 'movie_details.this_option_is_not_available' }),
      type: 'info',
    });
  };

  const sx: SxProps = {
    fontWeight: '700',
    fontSize: pxToRem(28),
    lineHeight: '32px',
    color: theme.palette.grey[90],
    textAlign: 'start',
  };

  return (
    <Box sx={{ boxShadow: 'none' }}>
      <Box
        sx={{
          padding: pxToRem(24),
          paddingTop: {
            mobile: pxToRem(30),
            desktop: pxToRem(54),
          },
        }}
      >
        <Typography
          sx={{
            ...sx,
            fontSize: pxToRem(28),
          }}
        >
          <FormattedMessage id='movie_details.filters' />
        </Typography>

        {isThereAtLeastOneAvailableShowTime &&
          filmCinemas?.cinemas?.length !== 0 &&
          !isOfferApplied && (
            <SelectedFilters
              selectedFilters={selectedFilters}
              seeResultsDisabled={seeResultsDisabled}
              handleDeleteSelectedFilter={removeSelectedFilter}
              clearFilters={handleClearFilters}
              submitFilters={submitFilters}
            />
          )}
      </Box>

      {isThereAtLeastOneAvailableShowTime &&
      filmCinemas?.cinemas?.length !== 0 &&
      !isOfferApplied ? (
        <>
          <Divider sx={dividerSx} />
          <CinemasFilter
            typographySx={typographySx}
            cinemas={movieCinemasFilter?.cinemas}
            selectedCinemasFilter={selectedCinemasFilter.map(({ id }) => id)}
            isFetchingCinemas={isFetchingCinemas}
            returnSelectedCinemas={(cinemasIds: number[]) =>
              setSelectedCinemasFilter(cinemasIds.map(id => ({ id, type: 'cinema' })))
            }
            showDisabledFilterMessage={showDisabledFilterMessage}
          />
          <Divider sx={dividerSx} />
          <ExperiencesFilter
            typographySx={typographySx}
            experiences={movieExperiencesFilter?.experiences}
            selectedExperiencesFilter={selectedExperiencesFilter.map(({ id }) => id)}
            isFetchingExperiences={isFetchingExperiences}
            returnSelectedExperiences={(experiencesIds: number[]) =>
              setSelectedExperiencesFilter(
                experiencesIds.map(id => ({ id, type: 'experience' }))
              )
            }
            showDisabledFilterMessage={showDisabledFilterMessage}
          />
        </>
      ) : (
        <Box
          sx={{
            height: '50vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              marginTop: pxToRem(80),
              marginBottom: pxToRem(24),
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ImageBoxFuture
              img={NoFilters}
              title={intl.formatMessage({
                id: 'alt.empty_filters',
              })}
              objectFit='contain'
              sx={{
                width: '74px',
                height: '84px',
              }}
            />
          </Box>
          <Typography
            sx={{
              color: theme.palette.grey[50],
              fontWeight: 400,
              fontSize: pxToRem(20),
              lineHeight: '30px',
              textAlign: 'center',
              padding: '10px',
            }}
          >
            {EmptyMsg}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MovieFilters;
